/**
 * Admin Ingredients-without-Brix page + Smart Ingredient Wizard.
 *
 * Two modes share one screen:
 *   1) Report mode (default) — read-only list of ingredients whose Brix
 *      cannot be resolved. Each row has the legacy "Sæt Brix / Opret"
 *      modal for ad-hoc one-off fixes.
 *   2) Wizard mode — fetches AI/fuzzy suggestions for every row, shows
 *      a color-coded match-score badge, lets the admin batch-approve
 *      many rows at once (aliases + creates + brix-sets in one POST),
 *      and offers to run Resync immediately afterwards.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import { API } from '../App';

// ── Tiny presentational helpers ────────────────────────────────────────────
const REASON_META = {
  not_in_catalog: { label: 'Findes ikke i kataloget', cls: 'bg-red-100 text-red-800 border-red-200' },
  catalog_brix_zero: { label: 'Katalog: brix = 0', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  catalog_brix_null: { label: 'Katalog: brix mangler', cls: 'bg-orange-100 text-orange-800 border-orange-200' },
};

const RECO_META = {
  recommended: { label: 'Anbefalet', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  likely: { label: 'Sandsynlig', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  uncertain: { label: 'Usikker', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
  create_new: { label: 'Opret ny', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
};

const scoreBadgeCls = (score) => {
  if (score >= 90) return 'bg-emerald-100 text-emerald-800 border-emerald-300';
  if (score >= 70) return 'bg-amber-100 text-amber-800 border-amber-300';
  if (score >= 50) return 'bg-rose-100 text-rose-800 border-rose-300';
  return 'bg-gray-100 text-gray-700 border-gray-300';
};

const EnvironmentBadge = ({ env }) => {
  const isPreview = env?.is_preview;
  const cls = isPreview ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-green-100 text-green-800 border-green-300';
  const label = isPreview ? 'PREVIEW' : 'PRODUCTION';
  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-bold tracking-wider ${cls}`}
      data-testid="ingr-missing-brix-env-badge"
      title={`MongoDB: ${env?.db_name || '?'}`}
    >
      <span className={`inline-block w-2 h-2 rounded-full ${isPreview ? 'bg-amber-500' : 'bg-green-500'}`} />
      {label}
      <span className="opacity-60 font-mono normal-case tracking-normal">{env?.db_name}</span>
    </span>
  );
};

const SummaryTile = ({ label, value, cls, testid }) => (
  <div className={`rounded-lg p-3 ${cls}`} data-testid={testid}>
    <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
    <div className="text-2xl font-bold tabular-nums">{value}</div>
  </div>
);

const AffectedRecipesCell = ({ recipes, ingredientName }) => {
  const [open, setOpen] = useState(false);
  if (!recipes || recipes.length === 0) return <span className="text-gray-400">—</span>;
  const visible = open ? recipes : recipes.slice(0, 2);
  return (
    <div className="text-xs space-y-0.5">
      {visible.map((r) => (
        <div key={r.id}>
          <Link to={`/recipes/${r.id}`} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
            {r.name}
          </Link>
        </div>
      ))}
      {recipes.length > 2 && (
        <button
          type="button"
          className="text-blue-600 hover:underline"
          onClick={() => setOpen(!open)}
          data-testid={`ingr-affected-toggle-${ingredientName}`}
        >
          {open ? 'Vis færre' : `+${recipes.length - 2} flere`}
        </button>
      )}
    </div>
  );
};

const fmtBrixCell = (v) => {
  if (v === null || v === undefined) return <span className="text-gray-400">null</span>;
  return <span className="tabular-nums">{Number(v).toFixed(2)}</span>;
};

const CATEGORIES = [
  'Sirup', 'Sodavand', 'Juice', 'Mixer', 'Alkohol', 'Mejeri',
  'Frugtpuré', 'Puré', 'Saftkoncentrat', 'Sukkerfri', 'Kaffe',
  'Energidrik', 'Basis', 'Additiv', 'Andet',
];

// ── Legacy single-row modal (kept for non-wizard ad-hoc fixes) ────────────
const FixIngredientModal = ({ row, onClose, onSaved }) => {
  const isCreate = !row.exists_in_catalog;
  const [brix, setBrix] = useState(
    row.suggested_brix !== null && row.suggested_brix !== undefined ? String(row.suggested_brix) : ''
  );
  const [category, setCategory] = useState(row.category || '');
  const [saving, setSaving] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);

  const brixNum = parseFloat(brix);
  const validBrix = !isNaN(brixNum) && brixNum >= 0 && brixNum <= 100;
  const canSubmit = validBrix && (!isCreate || (category && category.trim()));

  const save = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    try {
      if (isCreate) {
        await axios.post(`${API}/admin/ingredients`, {
          name: row.name, category: category.trim(),
          default_brix: brixNum, standard_brix: brixNum,
        });
        toast.success(`Oprettet '${row.name}' med Brix ${brixNum.toFixed(1)}°Bx`);
      } else {
        await axios.patch(`${API}/admin/ingredients/by-name/brix`, { name: row.name, brix: brixNum });
        toast.success(`Brix opdateret for '${row.name}' → ${brixNum.toFixed(1)}°Bx`);
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunne ikke gemme');
      setConfirmStep(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      onClick={onClose}
      data-testid="fix-ingredient-modal-backdrop"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="fix-ingredient-modal">
        <h3 className="text-lg font-bold text-gray-900 mb-1">{isCreate ? 'Opret ingrediens i katalog' : 'Sæt Brix-værdi'}</h3>
        <p className="text-xs text-gray-500 mb-4">
          {isCreate
            ? 'Ingrediensen findes ikke i master_ingredients. Vælg kategori og angiv Brix for at oprette den.'
            : 'Opdaterer både default_brix og standard_brix på den eksisterende katalogpost.'}
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Navn</label>
            <input type="text" value={row.name} readOnly disabled
              className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-sm font-medium cursor-not-allowed"
              data-testid="fix-modal-name" />
          </div>
          {isCreate && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Kategori *</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} disabled={saving || confirmStep}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white" data-testid="fix-modal-category">
                <option value="">— Vælg kategori —</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Brix-værdi (°Bx) *
              {row.suggested_brix !== null && row.suggested_brix !== undefined && (
                <span className="ml-2 font-normal text-gray-500">forslag: {Number(row.suggested_brix).toFixed(1)}</span>
              )}
            </label>
            <input type="number" min="0" max="100" step="0.1" value={brix}
              onChange={(e) => setBrix(e.target.value)} disabled={saving || confirmStep} autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm tabular-nums"
              data-testid="fix-modal-brix" placeholder="fx 0 for vand/alkohol, 65 for sirup" />
            {brix && !validBrix && <p className="text-xs text-red-600 mt-1">Brix skal være et tal mellem 0 og 100</p>}
          </div>
        </div>
        {confirmStep ? (
          <div className="mt-5 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <p className="text-sm text-amber-900 mb-3">
              {isCreate
                ? <>Opret <strong>{row.name}</strong> i kategorien <strong>{category}</strong> med Brix <strong>{brixNum.toFixed(1)}°Bx</strong>?</>
                : <>Opdater Brix for <strong>{row.name}</strong> til <strong>{brixNum.toFixed(1)}°Bx</strong>?</>}
            </p>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setConfirmStep(false)} disabled={saving}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm hover:bg-gray-50">Tilbage</button>
              <button type="button" onClick={save} disabled={saving}
                className="px-4 py-1.5 bg-green-600 text-white rounded-md text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
                data-testid="fix-modal-confirm-save">{saving ? 'Gemmer…' : 'Bekræft og gem'}</button>
            </div>
          </div>
        ) : (
          <div className="mt-5 flex gap-2 justify-end">
            <button type="button" onClick={onClose} className="px-3 py-1.5 border border-gray-300 rounded-md text-sm hover:bg-gray-50">Annullér</button>
            <button type="button" onClick={() => setConfirmStep(true)} disabled={!canSubmit}
              className="px-4 py-1.5 bg-[#115DA8] text-white rounded-md text-sm font-semibold hover:bg-[#0d4d8f] disabled:opacity-50"
              data-testid="fix-modal-next">{isCreate ? 'Opret…' : 'Sæt Brix…'}</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Wizard plan/preview modal — shows EXACTLY what will be applied ────────
const WizardConfirmModal = ({ actions, onCancel, onConfirm, applying }) => {
  const aliasCount = actions.filter((a) => a.type === 'alias').length;
  const createCount = actions.filter((a) => a.type === 'create').length;
  const brixCount = actions.filter((a) => a.type === 'set_brix').length;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4" onClick={onCancel} data-testid="wizard-confirm-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-gray-900 mb-1">Godkend handlinger</h3>
        <p className="text-xs text-gray-500 mb-4">
          {actions.length} handlinger klar: <strong>{aliasCount}</strong> alias·{' '}
          <strong>{createCount}</strong> opret · <strong>{brixCount}</strong> sæt brix
        </p>
        <div className="flex-1 overflow-auto border border-gray-200 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-700 sticky top-0">
              <tr>
                <th className="px-2 py-1.5 text-left">Type</th>
                <th className="px-2 py-1.5 text-left">Detaljer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {actions.map((a, i) => (
                <tr key={i}>
                  <td className="px-2 py-1.5 font-semibold uppercase tracking-wide whitespace-nowrap">
                    {a.type === 'alias' && <span className="text-purple-700">Alias</span>}
                    {a.type === 'create' && <span className="text-blue-700">Opret</span>}
                    {a.type === 'set_brix' && <span className="text-amber-700">Brix</span>}
                  </td>
                  <td className="px-2 py-1.5">
                    {a.type === 'alias' && <>{a.ingredient_name} → <strong>{a.canonical_name}</strong></>}
                    {a.type === 'create' && <>Opret <strong>{a.name}</strong> ({a.category}, brix={a.brix})</>}
                    {a.type === 'set_brix' && <>Sæt brix på <strong>{a.name}</strong> → {a.brix}</>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex gap-2 justify-end">
          <button type="button" onClick={onCancel} disabled={applying}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm hover:bg-gray-50" data-testid="wizard-confirm-cancel">
            Annullér
          </button>
          <button type="button" onClick={onConfirm} disabled={applying}
            className="px-4 py-1.5 bg-emerald-600 text-white rounded-md text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
            data-testid="wizard-confirm-apply">
            {applying ? 'Udfører…' : `Bekræft og udfør ${actions.length}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Post-apply "Run resync now?" prompt ───────────────────────────────────
const ResyncPromptModal = ({ summary, onClose, onGoToResync }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4" data-testid="resync-prompt-modal">
    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-2">✓ Handlinger gemt</h3>
      <ul className="text-sm text-gray-700 space-y-1 mb-4">
        <li>• Aliasser tilføjet: <strong>{summary.aliases_added}</strong></li>
        <li>• Ingredienser oprettet: <strong>{summary.ingredients_created}</strong></li>
        <li>• Brix opdateret: <strong>{summary.brix_updated}</strong></li>
        {summary.errors > 0 && <li className="text-red-700">• Fejl: <strong>{summary.errors}</strong></li>}
      </ul>
      <p className="text-sm text-gray-600 mb-4">
        Vil du køre <strong>Resync</strong> nu, så opskrifterne får de opdaterede Brix-værdier fra kataloget?
      </p>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onClose}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm hover:bg-gray-50" data-testid="resync-prompt-skip">
          Nej tak, gør det senere
        </button>
        <button type="button" onClick={onGoToResync}
          className="px-4 py-1.5 bg-[#115DA8] text-white rounded-md text-sm font-semibold hover:bg-[#0d4d8f]"
          data-testid="resync-prompt-go">
          Åbn Resync →
        </button>
      </div>
    </div>
  </div>
);

// ── Main page ──────────────────────────────────────────────────────────────
const AdminIngredientsMissingBrixPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [environment, setEnvironment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reasonFilter, setReasonFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [includeUnpublished, setIncludeUnpublished] = useState(false);
  const [includeTestData, setIncludeTestData] = useState(false);
  const [fixingRow, setFixingRow] = useState(null);
  const [refetchCounter, setRefetchCounter] = useState(0);

  // ── Wizard state ────────────────────────────────────────────────────────
  // suggestionsByName: { [name_lower]: { candidates, recommendation, ai_used } }
  const [suggestionsByName, setSuggestionsByName] = useState({});
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [aiFallbackEnabled, setAiFallbackEnabled] = useState(true);
  // selections: { [name_lower]: { action: 'alias'|'create'|'set_brix'|'skip', canonicalName?, brix?, category? } }
  const [selections, setSelections] = useState({});
  const [confirmActions, setConfirmActions] = useState(null);  // shows the confirm modal when non-null
  const [applying, setApplying] = useState(false);
  const [resyncPrompt, setResyncPrompt] = useState(null);     // {summary} after success

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await axios.get(`${API}/admin/ingredients-missing-brix`, {
          params: { include_unpublished: includeUnpublished, include_test_data: includeTestData },
        });
        if (cancelled) return;
        setRows(r.data.rows || []);
        setSummary(r.data.summary || null);
        setEnvironment(r.data.environment || null);
        // Reset wizard state when filters change so stale suggestions don't linger.
        setSuggestionsByName({});
        setSelections({});
      } catch (err) {
        toast.error('Kunne ikke hente rapporten');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [includeUnpublished, includeTestData, refetchCounter]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (reasonFilter !== 'all' && r.reason !== reasonFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(r.name || '').toLowerCase().includes(q)) return false;
    }
    return true;
  }), [rows, reasonFilter, search]);

  const fetchSuggestions = useCallback(async () => {
    setSuggestionsLoading(true);
    try {
      const r = await axios.get(`${API}/admin/ingredient-suggestions`, {
        params: {
          include_unpublished: includeUnpublished,
          include_test_data: includeTestData,
          use_ai_fallback: aiFallbackEnabled,
        },
      });
      const map = {};
      const initialSel = {};
      for (const s of (r.data.suggestions || [])) {
        map[s.name_lower] = s;
        // Pre-select for "recommended" rows so the admin only needs to confirm.
        if (s.recommendation === 'recommended' && s.candidates && s.candidates.length > 0) {
          initialSel[s.name_lower] = {
            action: 'alias',
            canonicalName: s.candidates[0].canonical_name,
            score: s.candidates[0].score,
            source: s.candidates[0].reason,
          };
        }
      }
      setSuggestionsByName(map);
      setSelections(initialSel);
      toast.success(
        `Hentet forslag · ${Object.keys(map).length} ingredienser · ${r.data.ai_calls_made} AI-kald`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunne ikke hente forslag');
    } finally {
      setSuggestionsLoading(false);
    }
  }, [includeUnpublished, includeTestData, aiFallbackEnabled]);

  // ── Selection helpers ───────────────────────────────────────────────────
  const setRowAction = (nameLower, action) => {
    setSelections((prev) => ({ ...prev, [nameLower]: { ...(prev[nameLower] || {}), ...action } }));
  };
  const clearRow = (nameLower) => {
    setSelections((prev) => {
      const n = { ...prev }; delete n[nameLower]; return n;
    });
  };
  const selectAllRecommended = () => {
    const next = { ...selections };
    for (const s of Object.values(suggestionsByName)) {
      if (s.recommendation === 'recommended' && s.candidates?.length) {
        next[s.name_lower] = {
          action: 'alias',
          canonicalName: s.candidates[0].canonical_name,
          score: s.candidates[0].score,
          source: s.candidates[0].reason,
        };
      }
    }
    setSelections(next);
  };
  const clearAllSelections = () => setSelections({});

  // Build the action list that will be sent to batch-approve.
  const buildActions = () => {
    const out = [];
    for (const [nameLower, sel] of Object.entries(selections)) {
      const row = rows.find((r) => r.name_lower === nameLower);
      if (!row || !sel.action || sel.action === 'skip') continue;
      if (sel.action === 'alias' && sel.canonicalName) {
        out.push({
          type: 'alias',
          ingredient_name: row.name,
          canonical_name: sel.canonicalName,
          source: sel.source || 'manual',
          confidence: sel.score || 0,
        });
      } else if (sel.action === 'create') {
        const brixNum = parseFloat(sel.brix);
        if (isNaN(brixNum)) continue;
        out.push({
          type: 'create',
          name: row.name,
          category: sel.category || 'Andet',
          brix: brixNum,
        });
      } else if (sel.action === 'set_brix') {
        const brixNum = parseFloat(sel.brix);
        if (isNaN(brixNum)) continue;
        out.push({ type: 'set_brix', name: row.name, brix: brixNum });
      }
    }
    return out;
  };

  const approveBatch = async () => {
    const acts = buildActions();
    if (acts.length === 0) {
      toast.error('Ingen handlinger valgt');
      return;
    }
    setConfirmActions(acts);
  };

  const confirmAndApply = async () => {
    if (!confirmActions || confirmActions.length === 0) return;
    setApplying(true);
    try {
      const r = await axios.post(`${API}/admin/ingredient-wizard/batch-approve`, {
        actions: confirmActions,
      });
      const sum = r.data?.summary || { aliases_added: 0, ingredients_created: 0, brix_updated: 0, errors: 0 };
      if (sum.errors > 0) {
        // Show errors in a toast (the modal still gets the prompt below).
        const failed = (r.data.results || []).filter((x) => x.status === 'error');
        toast.error(`${sum.errors} handlinger fejlede: ${failed.slice(0, 3).map((e) => e.error).join(' · ')}`);
      } else {
        toast.success(
          `✓ ${sum.aliases_added} alias · ${sum.ingredients_created} opret · ${sum.brix_updated} brix`
        );
      }
      setConfirmActions(null);
      setSelections({});
      setSuggestionsByName({});
      setRefetchCounter((n) => n + 1);
      setResyncPrompt({ summary: sum });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Batch-godkendelse fejlede');
    } finally {
      setApplying(false);
    }
  };

  const hasWizardData = Object.keys(suggestionsByName).length > 0;
  const selectionCount = Object.values(selections).filter((s) => s.action && s.action !== 'skip').length;

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl" data-testid="admin-ingredients-missing-brix-page">
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold text-gray-900">Ingredienser uden Brix</h1>
          {environment && <EnvironmentBadge env={environment} />}
        </div>
        <p className="text-gray-600">
          Analyse-rapport — hver ingrediens, hvis Brix ikke kan resolves fra hverken opskriften eller kataloget,
          aggregeret efter brug. Brug <strong>Smart Wizard</strong> nedenfor til at massehåndtere mange rækker
          på én gang via alias-mapping (fx <em>&quot;Apple Whisky&quot; → &quot;Whisky&quot;</em>) eller opret nye ingredienser direkte.
        </p>
        {environment?.is_preview && (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            ⚠️ Du kigger på <strong>preview-databasen</strong> (<code>{environment.db_name}</code>).
          </div>
        )}
      </div>

      {/* Backend-side filter toggles */}
      <div className="flex flex-wrap items-center gap-4 mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm">
        <span className="font-semibold text-blue-900">Datasæt:</span>
        <label className="flex items-center gap-2 cursor-pointer text-blue-900">
          <input type="checkbox" checked={includeTestData} onChange={(e) => setIncludeTestData(e.target.checked)}
            data-testid="ingr-missing-include-test" /> Inkluder testdata
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-blue-900">
          <input type="checkbox" checked={includeUnpublished} onChange={(e) => setIncludeUnpublished(e.target.checked)}
            data-testid="ingr-missing-include-unpublished" /> Inkluder upublicerede
        </label>
        {summary && (
          <span className="ml-auto text-xs text-blue-700">
            Skipped: {summary.skipped_test_data} testdata · {summary.skipped_unpublished} upublicerede
          </span>
        )}
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
          <SummaryTile label="Unikke ingredienser" value={summary.total_missing_ingredients} cls="bg-gray-50 text-gray-900" testid="ingr-summary-total" />
          <SummaryTile label="Ikke i katalog" value={summary.not_in_catalog} cls="bg-red-50 text-red-800" testid="ingr-summary-not-in-catalog" />
          <SummaryTile label="Brix=0" value={summary.catalog_brix_zero} cls="bg-amber-50 text-amber-800" testid="ingr-summary-brix-zero" />
          <SummaryTile label="Brix mangler" value={summary.catalog_brix_null} cls="bg-orange-50 text-orange-800" testid="ingr-summary-brix-null" />
          <SummaryTile label="Berørte rækker" value={summary.total_recipe_uses} cls="bg-blue-50 text-blue-800" testid="ingr-summary-uses" />
        </div>
      )}

      {/* Wizard control bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
        <span className="font-semibold text-purple-900">🪄 Smart Wizard:</span>
        <button
          type="button"
          onClick={fetchSuggestions}
          disabled={suggestionsLoading || loading || rows.length === 0}
          className="px-4 py-1.5 bg-purple-600 text-white rounded-md text-sm font-semibold hover:bg-purple-700 disabled:opacity-50"
          data-testid="wizard-fetch-suggestions-btn"
        >
          {suggestionsLoading ? 'Henter forslag…' : (hasWizardData ? 'Genberegn forslag' : 'Hent forslag')}
        </button>
        <label className="flex items-center gap-2 cursor-pointer text-purple-900 text-sm">
          <input type="checkbox" checked={aiFallbackEnabled} onChange={(e) => setAiFallbackEnabled(e.target.checked)}
            data-testid="wizard-ai-fallback-toggle" />
          AI-fallback (GPT-4o-mini, kun ved usikre lokale matches)
        </label>
        {hasWizardData && (
          <>
            <button
              type="button"
              onClick={selectAllRecommended}
              className="px-3 py-1.5 bg-emerald-600 text-white rounded-md text-xs font-semibold hover:bg-emerald-700"
              data-testid="wizard-select-recommended-btn"
            >
              ✓ Vælg alle anbefalede
            </button>
            <button
              type="button"
              onClick={clearAllSelections}
              className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md text-xs hover:bg-gray-50"
              data-testid="wizard-clear-selections-btn"
            >
              Ryd valg
            </button>
            <button
              type="button"
              onClick={approveBatch}
              disabled={selectionCount === 0}
              className="ml-auto px-4 py-1.5 bg-[#115DA8] text-white rounded-md text-sm font-semibold hover:bg-[#0d4d8f] disabled:opacity-50"
              data-testid="wizard-approve-btn"
            >
              Godkend valgte ({selectionCount})
            </button>
          </>
        )}
      </div>

      {/* Local filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-white border border-gray-200 rounded-lg">
        <input type="text" placeholder="Søg ingrediensnavn…" value={search} onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm flex-1 min-w-[180px]" data-testid="ingr-missing-search" />
        <select value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm" data-testid="ingr-missing-reason-filter">
          <option value="all">Alle årsager</option>
          <option value="not_in_catalog">Ikke i katalog</option>
          <option value="catalog_brix_zero">Brix=0</option>
          <option value="catalog_brix_null">Brix mangler</option>
        </select>
        <span className="text-xs text-gray-500 ml-auto" data-testid="ingr-missing-count">{filtered.length} af {rows.length}</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="p-8 text-center text-gray-500">Indlæser…</div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-700">
              <tr>
                {hasWizardData && <th className="px-2 py-2 font-semibold w-8">✓</th>}
                <th className="px-3 py-2 font-semibold">Ingrediens</th>
                <th className="px-3 py-2 font-semibold text-right">brug</th>
                <th className="px-3 py-2 font-semibold">Påvirkede opskrifter</th>
                {hasWizardData ? (
                  <>
                    <th className="px-3 py-2 font-semibold">Foreslået match</th>
                    <th className="px-3 py-2 font-semibold">Handling</th>
                  </>
                ) : (
                  <>
                    <th className="px-3 py-2 font-semibold text-right">Brix-forslag</th>
                    <th className="px-3 py-2 font-semibold">Årsag</th>
                    <th className="px-3 py-2 font-semibold">Handling</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((r) => {
                const sugg = suggestionsByName[r.name_lower];
                const sel = selections[r.name_lower] || {};
                const meta = REASON_META[r.reason] || REASON_META.catalog_brix_null;
                return (
                  <tr key={r.name_lower} data-testid={`ingr-missing-row-${r.name_lower}`} className="hover:bg-gray-50 align-top">
                    {hasWizardData && (
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={!!sel.action && sel.action !== 'skip'}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // default to alias if a top candidate exists, otherwise create
                              if (sugg?.candidates?.length) {
                                setRowAction(r.name_lower, {
                                  action: 'alias',
                                  canonicalName: sugg.candidates[0].canonical_name,
                                  score: sugg.candidates[0].score,
                                  source: sugg.candidates[0].reason,
                                });
                              } else {
                                setRowAction(r.name_lower, {
                                  action: 'create',
                                  brix: r.suggested_brix ?? 0,
                                  category: r.category || 'Andet',
                                });
                              }
                            } else {
                              clearRow(r.name_lower);
                            }
                          }}
                          data-testid={`wizard-row-checkbox-${r.name_lower}`}
                        />
                      </td>
                    )}
                    <td className="px-3 py-2 font-medium text-gray-900">
                      {r.name}
                      <div className="text-[10px] uppercase tracking-wide text-gray-400">
                        {r.exists_in_catalog ? 'I katalog' : 'Ikke i katalog'}
                        {r.category && <> · kat: {r.category}</>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">{r.usage_count}</td>
                    <td className="px-3 py-2">
                      <AffectedRecipesCell recipes={r.affected_recipes} ingredientName={r.name_lower} />
                    </td>

                    {hasWizardData ? (
                      <>
                        {/* Suggested match column */}
                        <td className="px-3 py-2">
                          {sugg?.candidates?.length > 0 ? (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className={`inline-block px-2 py-0.5 rounded text-xs border tabular-nums ${scoreBadgeCls(sugg.candidates[0].score)}`}>
                                  {sugg.candidates[0].score}%
                                </span>
                                <span className="font-medium">{sugg.candidates[0].canonical_name}</span>
                                {sugg.candidates[0].reason === 'ai' && (
                                  <span className="text-[10px] uppercase bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded">AI</span>
                                )}
                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] border ${RECO_META[sugg.recommendation]?.cls || ''}`}>
                                  {RECO_META[sugg.recommendation]?.label || sugg.recommendation}
                                </span>
                              </div>
                              {sugg.candidates.length > 1 && (
                                <details className="text-xs text-gray-600">
                                  <summary className="cursor-pointer hover:text-gray-900">
                                    +{sugg.candidates.length - 1} alternativ{sugg.candidates.length - 1 > 1 ? 'er' : ''}
                                  </summary>
                                  <div className="mt-1 space-y-0.5 pl-2 border-l-2 border-gray-200">
                                    {sugg.candidates.slice(1).map((c, i) => (
                                      <button
                                        key={i}
                                        type="button"
                                        onClick={() => setRowAction(r.name_lower, {
                                          action: 'alias', canonicalName: c.canonical_name, score: c.score, source: c.reason,
                                        })}
                                        className="block text-left hover:bg-gray-50 px-1 rounded w-full"
                                      >
                                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] border ${scoreBadgeCls(c.score)} mr-1 tabular-nums`}>
                                          {c.score}%
                                        </span>
                                        {c.canonical_name}
                                      </button>
                                    ))}
                                  </div>
                                </details>
                              )}
                              {sugg.candidates[0].ai_reason && (
                                <p className="text-[10px] text-purple-700 italic">{sugg.candidates[0].ai_reason}</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">Intet match — opret ny</span>
                          )}
                        </td>

                        {/* Action column */}
                        <td className="px-3 py-2">
                          <select
                            value={sel.action || 'skip'}
                            onChange={(e) => {
                              const a = e.target.value;
                              if (a === 'skip') { clearRow(r.name_lower); return; }
                              if (a === 'alias') {
                                setRowAction(r.name_lower, {
                                  action: 'alias',
                                  canonicalName: sel.canonicalName || sugg?.candidates?.[0]?.canonical_name || '',
                                  score: sel.score || sugg?.candidates?.[0]?.score || 0,
                                  source: sel.source || sugg?.candidates?.[0]?.reason || 'manual',
                                });
                              } else if (a === 'create') {
                                setRowAction(r.name_lower, {
                                  action: 'create',
                                  brix: sel.brix ?? r.suggested_brix ?? 0,
                                  category: sel.category || r.category || 'Andet',
                                });
                              } else if (a === 'set_brix') {
                                setRowAction(r.name_lower, {
                                  action: 'set_brix',
                                  brix: sel.brix ?? r.suggested_brix ?? 0,
                                });
                              }
                            }}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                            data-testid={`wizard-row-action-${r.name_lower}`}
                          >
                            <option value="skip">Spring over</option>
                            <option value="alias">Map som alias</option>
                            <option value="create">Opret ny ingrediens</option>
                            {r.exists_in_catalog && <option value="set_brix">Sæt Brix på eksisterende</option>}
                          </select>

                          {sel.action === 'create' && (
                            <div className="mt-1 space-y-1">
                              <select
                                value={sel.category || 'Andet'}
                                onChange={(e) => setRowAction(r.name_lower, { category: e.target.value })}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                              >
                                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                              </select>
                              <input
                                type="number" min="0" max="100" step="0.1" placeholder="Brix"
                                value={sel.brix ?? ''}
                                onChange={(e) => setRowAction(r.name_lower, { brix: e.target.value })}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded tabular-nums"
                              />
                            </div>
                          )}
                          {sel.action === 'set_brix' && (
                            <input
                              type="number" min="0" max="100" step="0.1" placeholder="Brix"
                              value={sel.brix ?? ''}
                              onChange={(e) => setRowAction(r.name_lower, { brix: e.target.value })}
                              className="mt-1 w-full px-2 py-1 text-xs border border-gray-300 rounded tabular-nums"
                            />
                          )}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {r.suggested_brix === null || r.suggested_brix === undefined ? (
                            <span className="text-gray-400">—</span>
                          ) : (
                            <span className="inline-block px-2 py-0.5 rounded bg-green-50 text-green-800 border border-green-200">
                              {Number(r.suggested_brix).toFixed(1)}°Bx
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs border ${meta.cls}`}>{meta.label}</span>
                        </td>
                        <td className="px-3 py-2">
                          <button type="button" onClick={() => setFixingRow(r)}
                            className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap text-white ${r.exists_in_catalog ? 'bg-[#115DA8] hover:bg-[#0d4d8f]' : 'bg-red-600 hover:bg-red-700'}`}
                            data-testid={`fix-ingredient-btn-${r.name_lower}`}>
                            {r.exists_in_catalog ? 'Sæt Brix' : 'Opret i katalog'}
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan={hasWizardData ? 6 : 7} className="px-3 py-8 text-center text-gray-500">
                    Ingen rækker matcher filtrene.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-4 text-xs text-gray-500">
        Heuristisk Brix-forslag er kun et udgangspunkt baseret på navnemønstre. AI-fallback (GPT-4o-mini) bruges
        kun til ingredienser hvor lokalt fuzzy-match ikke giver et match over 70 %. <strong>Ingen ændringer
        sker uden din eksplicitte bekræftelse.</strong>
      </p>

      {/* Legacy single-row modal */}
      {fixingRow && (
        <FixIngredientModal row={fixingRow} onClose={() => setFixingRow(null)} onSaved={() => setRefetchCounter((n) => n + 1)} />
      )}
      {/* Wizard confirm modal */}
      {confirmActions && (
        <WizardConfirmModal
          actions={confirmActions}
          applying={applying}
          onCancel={() => setConfirmActions(null)}
          onConfirm={confirmAndApply}
        />
      )}
      {/* Post-apply resync prompt */}
      {resyncPrompt && (
        <ResyncPromptModal
          summary={resyncPrompt.summary}
          onClose={() => setResyncPrompt(null)}
          onGoToResync={() => { setResyncPrompt(null); navigate('/admin/resync-ingredient-brix'); }}
        />
      )}
    </div>
  );
};

export default AdminIngredientsMissingBrixPage;
